import { Button, cn } from "@ndd/ui";
import {
  Check,
  CircleAlert,
  ClipboardPaste,
  Clock,
  Loader2,
  Sparkles,
  TriangleAlert,
  type LucideIcon,
} from "lucide-react";
import React, { type ReactElement, type ReactNode } from "react";
import type { EnhanceFooterState } from "../../lib/appointment-workspace-state";
import { EditorFooter } from "./editor-status";

/**
 * The Appointment note footer: where Enhance lives and what it is waiting
 * for. One sentence and at most one action per state, so the RD never has
 * to decode a badge. The draft itself opens in its own tab.
 */
export function EnhanceFooter({
  state,
  reconnecting,
  onEnhance,
  onRefresh,
  onOpenDraft,
  onPasteTranscript,
  onReEnhance,
  autoEnhanceHint,
}: {
  state: EnhanceFooterState;
  reconnecting: boolean;
  onEnhance: () => void;
  onRefresh: () => void;
  onOpenDraft: () => void;
  /**
   * NO-775: offers "Paste transcript" while the transcript is still missing.
   * The workspace passes it only when `manual-soap-generation` is on.
   */
  onPasteTranscript?: () => void;
  /**
   * NO-785: offers "Re-enhance" beside Open draft and after a failed run. The
   * workspace passes it only when `soap-re-enhance` is on and the Draft was
   * never approved.
   */
  onReEnhance?: () => void;
  /**
   * NO-428: the ready status says Enhance can wait until the RD is done. The
   * workspace passes it only when soap-enhancement and soap-auto-enhance are
   * both on.
   */
  autoEnhanceHint?: boolean;
}): ReactElement {
  return (
    <EditorFooter className="gap-1.5">
      <div className="flex min-h-8 flex-wrap items-center gap-x-3 gap-y-2">
        <FooterBody
          state={state}
          onEnhance={onEnhance}
          onRefresh={onRefresh}
          onOpenDraft={onOpenDraft}
          {...(onPasteTranscript ? { onPasteTranscript } : {})}
          {...(onReEnhance ? { onReEnhance } : {})}
          {...(autoEnhanceHint ? { autoEnhanceHint } : {})}
        />
      </div>
      {reconnecting ? (
        <p className="text-xs text-warning" role="status">
          Connection lost. Showing the last known status and retrying…
        </p>
      ) : null}
    </EditorFooter>
  );
}

type FooterHandlers = {
  onEnhance: () => void;
  onRefresh: () => void;
  onOpenDraft: () => void;
  onPasteTranscript?: () => void;
  onReEnhance?: () => void;
  autoEnhanceHint?: boolean;
};

function FooterBody({
  state,
  ...handlers
}: { state: EnhanceFooterState } & FooterHandlers): ReactElement {
  switch (state.kind) {
    case "checking":
    case "waiting":
    case "transcript_failed":
    case "transcript_conflict":
      return <TranscriptFooter state={state} {...handlers} />;
    case "unavailable":
    case "retry":
    case "ready":
      return <StartFooter state={state} {...handlers} />;
    case "enhancing":
    case "generation_failed":
    case "draft_ready":
      return <RunFooter state={state} {...handlers} />;
    case "reenhancing":
    case "reenhance_failed":
      return <ReEnhanceFooter state={state} {...handlers} />;
  }
}

/** Before Enhance can start: the transcript is missing, late, or in trouble. */
function TranscriptFooter({
  state,
  onRefresh,
  onPasteTranscript,
}: {
  state: Extract<
    EnhanceFooterState,
    { kind: "checking" | "waiting" | "transcript_failed" | "transcript_conflict" }
  >;
} & FooterHandlers): ReactElement {
  switch (state.kind) {
    case "checking":
      return (
        <Status icon={Loader2} iconClassName="animate-spin">
          Checking for the transcript…
        </Status>
      );
    case "waiting":
      return (
        <WaitingFooter onRefresh={onRefresh} onPasteTranscript={onPasteTranscript} />
      );
    case "transcript_failed":
      return (
        <>
          <Status icon={CircleAlert} tone="danger">
            The transcript for this visit failed to process. Enhance stays locked
            without it.
          </Status>
          <Button type="button" variant="outline" size="sm" onClick={onRefresh}>
            Check again
          </Button>
        </>
      );
    case "transcript_conflict":
      return (
        <>
          <Status icon={TriangleAlert} tone="warning">
            Two versions of this transcript don&apos;t match. Enhance stays locked until
            it settles.
          </Status>
          <Button type="button" variant="outline" size="sm" onClick={onRefresh}>
            Check again
          </Button>
        </>
      );
  }
}

/** No transcript yet. NO-775: Paste transcript appears only when a handler is given. */
function WaitingFooter({
  onRefresh,
  onPasteTranscript,
}: {
  onRefresh: () => void;
  onPasteTranscript: (() => void) | undefined;
}): ReactElement {
  return (
    <>
      <Status icon={Clock}>
        Waiting for the transcript from Healthie · usually 1–2 minutes after the visit
        ends
      </Status>
      <Button type="button" variant="ghost" size="sm" onClick={onRefresh}>
        Check again
      </Button>
      {onPasteTranscript ? (
        <Button type="button" variant="outline" size="sm" onClick={onPasteTranscript}>
          <ClipboardPaste aria-hidden="true" />
          Paste transcript
        </Button>
      ) : null}
      <EnhanceButton disabled />
    </>
  );
}

/** The transcript is ready: start, retry, or explain why Enhance is unavailable. */
function StartFooter({
  state,
  onEnhance,
  autoEnhanceHint,
}: {
  state: Extract<EnhanceFooterState, { kind: "unavailable" | "retry" | "ready" }>;
} & FooterHandlers): ReactElement {
  switch (state.kind) {
    case "unavailable":
      return (
        <Status icon={CircleAlert} tone="warning">
          Enhance isn&apos;t available right now. Your appointment note is saved.
        </Status>
      );
    case "retry":
      return (
        <>
          <Status icon={CircleAlert} tone="danger">
            Enhance didn&apos;t start. The transcript is still ready.
          </Status>
          <Button type="button" variant="outline" size="sm" onClick={onEnhance}>
            Try again
          </Button>
        </>
      );
    case "ready":
      return (
        <>
          <Status icon={Check} tone="success">
            {autoEnhanceHint ? (
              <ReadyHint />
            ) : (
              <>
                Transcript ready. Enhance notes builds a draft from today&apos;s
                transcript, your appointment note, and the last signed note.
              </>
            )}
          </Status>
          <EnhanceButton
            onClick={onEnhance}
            disabled={state.requesting}
            label={state.requesting ? "Starting…" : "Enhance notes"}
            pulse={!state.requesting}
          />
        </>
      );
  }
}

/** NO-428: the approved ready line, then the existing explanation. */
function ReadyHint(): ReactElement {
  return (
    <>
      <span className="block">Transcript ready. Enhance when you&apos;re done.</span>
      <span className="block">
        Enhance notes builds a draft from today&apos;s transcript, your appointment
        note, and the last signed note.
      </span>
    </>
  );
}

/** An Enhancement exists: running (NO-772: Running, then Writing), failed, or ready. */
function RunFooter({
  state,
  onEnhance,
  onOpenDraft,
  onReEnhance,
}: {
  state: Extract<
    EnhanceFooterState,
    { kind: "enhancing" | "generation_failed" | "draft_ready" }
  >;
} & FooterHandlers): ReactElement {
  switch (state.kind) {
    case "enhancing":
      return (
        <>
          <Status icon={Loader2} iconClassName="animate-spin" tone="ai">
            {state.writing ? "Writing" : "Running"}
          </Status>
          <Button type="button" variant="ghost" size="sm" onClick={onOpenDraft}>
            Open draft
          </Button>
        </>
      );
    case "generation_failed":
      return (
        <>
          <Status icon={CircleAlert} tone="danger">
            Enhance didn&apos;t finish. Nothing was saved to the draft.
          </Status>
          {state.retryable ? (
            <Button type="button" variant="outline" size="sm" onClick={onEnhance}>
              Try again
            </Button>
          ) : null}
        </>
      );
    case "draft_ready":
      return (
        <>
          <Status icon={Sparkles} tone="ai">
            Your draft is ready in the Enhanced SOAP draft tab.
          </Status>
          <Button type="button" variant="ghost" size="sm" onClick={onOpenDraft}>
            Open draft
          </Button>
          {onReEnhance ? <ReEnhanceButton onClick={onReEnhance} /> : null}
        </>
      );
  }
}

/** NO-785: a Re-enhance is running (Running, then Writing) or did not finish. */
function ReEnhanceFooter({
  state,
  onOpenDraft,
  onReEnhance,
}: {
  state: Extract<EnhanceFooterState, { kind: "reenhancing" | "reenhance_failed" }>;
} & FooterHandlers): ReactElement {
  if (state.kind === "reenhancing") {
    return (
      <>
        <Status icon={Loader2} iconClassName="animate-spin" tone="ai">
          {state.writing ? "Writing" : "Running"}
        </Status>
        <Button type="button" variant="ghost" size="sm" onClick={onOpenDraft}>
          Open draft
        </Button>
      </>
    );
  }

  return (
    <>
      <Status icon={CircleAlert} tone="danger">
        Re-enhance didn&apos;t finish. Your draft is unchanged.
      </Status>
      <Button type="button" variant="ghost" size="sm" onClick={onOpenDraft}>
        Open draft
      </Button>
      {onReEnhance ? <ReEnhanceButton onClick={onReEnhance} /> : null}
    </>
  );
}

function ReEnhanceButton({ onClick }: { onClick: () => void }): ReactElement {
  return (
    <Button type="button" variant="outline" size="sm" onClick={onClick}>
      <Sparkles aria-hidden="true" />
      Re-enhance
    </Button>
  );
}

function Status({
  icon: Icon,
  iconClassName,
  tone = "muted",
  children,
}: {
  icon: LucideIcon;
  iconClassName?: string;
  tone?: "muted" | "success" | "warning" | "danger" | "ai";
  children: ReactNode;
}): ReactElement {
  const iconTone = {
    muted: "text-muted-foreground",
    success: "text-success",
    warning: "text-warning",
    danger: "text-danger",
    ai: "text-ai",
  }[tone];

  return (
    <p
      role="status"
      className="flex min-w-0 flex-1 basis-56 items-start gap-1.5 text-body-sm leading-4 text-muted-foreground"
    >
      <Icon
        aria-hidden="true"
        className={cn("mt-px size-3.5 shrink-0", iconTone, iconClassName)}
      />
      <span>{children}</span>
    </p>
  );
}

function EnhanceButton({
  onClick,
  disabled = false,
  label = "Enhance notes",
  pulse = false,
}: {
  onClick?: () => void;
  disabled?: boolean;
  label?: string;
  pulse?: boolean;
}): ReactElement {
  return (
    <Button
      type="button"
      size="sm"
      disabled={disabled}
      onClick={onClick}
      className={cn("ml-auto", pulse && "enhance-ready-pulse")}
    >
      <Sparkles aria-hidden="true" />
      {label}
    </Button>
  );
}
